<template>
  <v-dialog
    :value="value"
    max-width="600"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card style="max-height: 90vh">
      <v-card-title class="text-subtitle-1 flex-wrap">
        予約編集
        <!-- 現在ステータスのバッジ + 変更セレクト（編集時のみ。卓削除後の復旧等） -->
        <v-chip
          v-if="isEdit"
          small
          :color="statusBadgeColor"
          text-color="white"
          class="ms-3"
        >
          {{ statusBadgeLabel }}
        </v-chip>
        <v-select
          v-if="isEdit && statusChangeOptions.length > 1"
          v-model="form.status"
          :items="statusChangeOptions"
          outlined
          dense
          hide-details
          class="ms-3 status-select"
        />
        <v-spacer />
        <v-btn
          icon
          @click="close"
        >
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <!-- pt-4: scrollableダイアログのcard-textは上paddingがなく、先頭行のoutlinedラベルが刈られるため -->
      <v-card-text class="pt-4 px-6">
        <div class="form-section-label mt-0">
          日時
        </div>
        <v-row>
          <!-- 日付 -->
          <v-col cols="6">
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  :value="form.reservedDate"
                  label="日付"
                  readonly
                  outlined
                  dense
                  v-bind="attrs"
                  v-on="on"
                />
              </template>
              <v-date-picker
                :value="form.reservedDate"
                locale="ja"
                :day-format="date => new Date(date).getDate()"
                @input="form.reservedDate = $event; dateMenu = false"
              />
            </v-menu>
          </v-col>

          <!-- 人数 -->
          <v-col cols="6">
            <v-text-field
              v-model.number="form.customerCount"
              label="人数"
              type="number"
              min="1"
              outlined
              dense
            />
          </v-col>
        </v-row>

        <v-row>
          <!-- 開始時間 -->
          <v-col cols="6">
            <v-select
              v-model="form.startTime"
              :items="timeOptions"
              label="開始時間"
              outlined
              dense
            />
          </v-col>

          <!-- 終了時間 -->
          <v-col cols="6">
            <v-select
              v-model="form.endTime"
              :items="[{ text: '未設定', value: null }, ...timeOptions]"
              label="終了予定（任意・目安）"
              outlined
              dense
              clearable
            />
          </v-col>
        </v-row>
        <div class="table-later-hint">
          卓は来店時に選びます（卓作成ウィザード）。終了予定は空でもOK
        </div>

        <!-- 指名（複数キャスト×指名種別）とコース -->
        <div class="form-section-label">
          指名・コース<template v-if="form.nominations.length > 0">
            （指名{{ form.nominations.length }}件 設定済み）
          </template>
        </div>
        <div
          v-if="form.nominations.length > 0"
          class="d-flex flex-wrap align-center mb-4 nomination-chips"
        >
          <v-chip
            v-for="(row, i) in form.nominations"
            :key="`nom-${row.userId}-${row.nominationId}`"
            color="primary"
            close
            @click:close="form.nominations.splice(i, 1)"
          >
            <b class="me-1">{{ castNameOf(row.userId) }}</b>・{{ nominationNameOf(row.nominationId) }}
          </v-chip>
          <v-btn
            v-if="!showNominationInput"
            x-small
            text
            color="primary"
            @click="showNominationInput = true"
          >
            ＋ 指名を追加
          </v-btn>
        </div>
        <v-row
          v-if="form.nominations.length === 0 || showNominationInput"
          dense
          class="mb-2"
        >
          <v-col cols="5">
            <v-select
              v-model="nominationDraft.userId"
              :items="castOptions"
              label="キャスト"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col cols="5">
            <v-select
              v-model="nominationDraft.nominationId"
              :items="nominationOptions"
              label="指名種別"
              outlined
              dense
              hide-details
              clearable
            />
          </v-col>
          <v-col
            cols="2"
            class="d-flex align-center"
          >
            <v-btn
              small
              outlined
              block
              :disabled="!nominationDraft.userId || !nominationDraft.nominationId"
              @click="addNomination"
            >
              追加
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mt-1">
          <!-- コース（指名がある場合は必須: 来店処理のセット計上に使われる） -->
          <v-col cols="6">
            <v-select
              v-model="form.courseId"
              :items="courseOptions"
              :label="courseRequired ? 'コース（必須）' : 'コース'"
              :error="courseMissing"
              outlined
              dense
              clearable
            />
          </v-col>
        </v-row>
        <div
          v-if="courseMissing || checkInBlockedByCourse"
          class="course-required-hint mb-4"
        >
          <v-icon
            size="16"
            color="error"
          >
            {{ icons.mdiAlertCircleOutline }}
          </v-icon>
          <span v-if="courseMissing">指名がある場合はコースが必須です（未選択のままだと保存できません）</span>
          <span v-else>コースが未選択のため「卓を作成」を押せません。コースを選択して保存してください（終了予定・セット計上に使われます）</span>
        </div>

        <!-- 詳細（任意項目）: 入力がある場合は開いた状態で初期表示する -->
        <button
          type="button"
          class="detail-toggle"
          @click="isDetailOpen = !isDetailOpen"
        >
          詳細
          <span class="detail-toggle__count">{{ detailSummary }}</span>
          <v-icon
            size="18"
            class="detail-toggle__caret"
            :class="{ 'detail-toggle__caret--open': isDetailOpen }"
          >
            {{ icons.mdiChevronDown }}
          </v-icon>
        </button>
        <v-expand-transition>
          <div v-show="isDetailOpen">
            <v-row>
              <!-- 紹介者 -->
              <v-col cols="6">
                <v-select
                  v-model="form.referralId"
                  :items="referralOptions"
                  label="紹介者"
                  outlined
                  dense
                  clearable
                />
              </v-col>
            </v-row>

            <!-- 顧客タグ -->
            <v-select
              v-model="form.customerTagIds"
              :items="customerTagOptions"
              label="顧客タグ"
              outlined
              dense
              multiple
              chips
              small-chips
              deletable-chips
            />

            <!-- 顧客 -->
            <div class="text-subtitle-2 mb-2">
              顧客
            </div>
            <customer-preview
              v-for="customer in form.customers"
              :key="customer.id"
              :customer="customer"
              @remove="removeCustomer(customer.id)"
            />
            <v-btn
              small
              outlined
              class="mb-4"
              @click="customerSelectOpen = true"
            >
              <v-icon
                small
                class="mr-1"
              >
                {{ icons.mdiPlus }}
              </v-icon>
              顧客を追加
            </v-btn>

            <!-- メモ -->
            <v-textarea
              v-model="form.memo"
              label="メモ"
              outlined
              dense
              rows="2"
              auto-grow
            />
          </div>
        </v-expand-transition>

        <customer-select-dialog
          v-model="customerSelectOpen"
          :selected-ids="form.customers.map(c => c.id)"
          @select="addCustomer"
        />

        <!-- キャンセル理由（編集時・キャンセル操作時） -->
        <v-textarea
          v-if="showCancelReason"
          v-model="form.cancelReason"
          label="キャンセル理由"
          outlined
          dense
          rows="2"
        />
      </v-card-text>

      <v-card-actions class="px-4 py-3">
        <!-- 破壊系は左に小さく、操作系は右に -->
        <v-btn
          small
          color="error"
          text
          :loading="isSaving"
          @click="deleteReservation"
        >
          削除
        </v-btn>
        <v-btn
          v-if="canCancel"
          small
          color="warning"
          text
          :loading="isSaving"
          @click="cancelReservation"
        >
          キャンセル
        </v-btn>
        <v-spacer />
        <v-btn
          text
          @click="close"
        >
          閉じる
        </v-btn>
        <v-btn
          v-if="canCheckIn"
          color="success"
          :disabled="!form.courseId"
          :loading="isSaving"
          @click="requestCheckIn"
        >
          卓を作成
        </v-btn>
        <v-btn
          outlined
          color="primary"
          :disabled="courseMissing"
          :loading="isSaving"
          @click="save"
        >
          保存
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiClose, mdiPlus, mdiChevronDown, mdiAlertCircleOutline,
} from '@mdi/js'
import ReservationApi from '@/api/waiter/Reservation'
import WorkScheduleApi from '@/api/waiter/WorkSchedule'
import { RESERVATION_STATUS_COLOR } from '@/views/waiter/reservations/reservationDisplayUtils'
import { getBusinessTodayStr } from '@/views/waiter/floorUtils'
import useCurrentData from '@/views/composable/useCurrentData'
import {
  buildReservationParams,
  commitNominationDraft,
} from '@/views/waiter/reservations/reservationPayload'
import CustomerPreview from './CustomerPreview.vue'
import CustomerSelectDialog from './CustomerSelectDialog.vue'

const STATUS_LABEL = {
  confirmed: '確定',
  checked_in: '来店中',
  completed: '完了',
  canceled: 'キャンセル',
}

// 手動で選べる遷移先（BEの遷移表のうち、来店処理・キャンセルは専用ボタンに委ねる）
const MANUAL_STATUS_TRANSITIONS = {
  confirmed: [],
  checked_in: ['confirmed', 'completed'],
  completed: [],
  canceled: [],
}

export default {
  name: 'ReservationDialog',
  components: {
    CustomerPreview,
    CustomerSelectDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    reservation: {
      type: Object,
      default: null,
    },
    casts: {
      type: Array,
      default: () => [],
    },
    courses: {
      type: Array,
      default: () => [],
    },
    referrals: {
      type: Array,
      default: () => [],
    },
    // 指名種別（Nomination vendibles の生データ）
    nominations: {
      type: Array,
      default: () => [],
    },
    customerTags: {
      type: Array,
      default: () => [],
    },
    defaultDate: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      icons: {
        mdiClose, mdiPlus, mdiChevronDown, mdiAlertCircleOutline,
      },
      form: this.getInitialForm(),
      dateMenu: false,
      customerSelectOpen: false,
      showCancelReason: false,
      // 指名の追加入力行の表示（設定済みがある場合は「＋指名を追加」で開く）
      showNominationInput: false,
      isSaving: false,
      nominationDraft: { userId: null, nominationId: null },
      // 予約日の出勤予定キャスト（選択肢の並び順に使用）
      scheduledCastIds: [],
      // 詳細（任意項目）の開閉。入力がある場合は開いた状態で初期化する
      isDetailOpen: false,
    }
  },
  computed: {
    isEdit() {
      return !!this.reservation
    },
    statusBadgeLabel() {
      return STATUS_LABEL[this.reservation?.attributes?.status] || ''
    },
    statusBadgeColor() {
      return RESERVATION_STATUS_COLOR[this.reservation?.attributes?.status] || 'grey'
    },
    // 指名がある予約はコース必須（来店処理のセット計上に使われるため）。
    // キャストアプリ経由の予約作成にはこのルールを適用しない（店舗側フォームのみの制約）
    courseRequired() {
      return this.form.nominations.length > 0
    },
    courseMissing() {
      return this.courseRequired && !this.form.courseId
    },
    // 卓作成（来店処理）はコース必須。ボタンをただ無効にせず理由を表示する
    checkInBlockedByCourse() {
      return this.canCheckIn && !this.form.courseId
    },
    // 詳細セクションの入力済みサマリー（「入力済み 2件: タグ・メモ」）
    detailFilledLabels() {
      const labels = []
      if (this.form.referralId) labels.push('紹介者')
      if (this.form.customerTagIds.length > 0) labels.push('タグ')
      if (this.form.customers.length > 0) labels.push('顧客')
      if ((this.form.memo || '').trim() !== '') labels.push('メモ')

      return labels
    },
    detailSummary() {
      if (this.detailFilledLabels.length === 0) return '（紹介者・タグ・顧客・メモ）'

      return `（入力済み ${this.detailFilledLabels.length}件: ${this.detailFilledLabels.join('・')}）`
    },
    canCancel() {
      return this.reservation?.attributes?.status === 'confirmed'
    },
    statusChangeOptions() {
      const current = this.reservation?.attributes?.status
      if (!current) return []
      const targets = MANUAL_STATUS_TRANSITIONS[current] || []

      return [current, ...targets].map(s => ({ text: STATUS_LABEL[s] || s, value: s }))
    },
    canCheckIn() {
      if (this.reservation?.attributes?.status !== 'confirmed') return false

      // 卓作成は予約日=当営業日のときのみ
      const { currentClub } = useCurrentData()
      const reservedDate = String(this.reservation.attributes?.reservedDate || '').slice(0, 10)

      return reservedDate === getBusinessTodayStr(new Date(), currentClub.value?.openAt)
    },
    timeOptions() {
      const options = []
      for (let h = 16; h <= 29; h += 1) {
        for (let m = 0; m < 60; m += 30) {
          const displayH = h >= 24 ? h - 24 : h
          const label = `${String(displayH).padStart(2, '0')}:${String(m).padStart(2, '0')}`
          options.push({ text: label, value: label })
        }
      }

      return options
    },
    castOptions() {
      // 予約日の出勤予定者を先頭に並べる（（出勤）表記付き）
      const scheduled = new Set(this.scheduledCastIds.map(Number))
      const options = this.casts.map(c => {
        const id = parseInt(c.id, 10)
        const name = c.attributes?.name || c.name
        const isScheduled = scheduled.has(id)

        return {
          text: isScheduled ? `${name}（出勤）` : name,
          value: id,
          isScheduled,
        }
      })

      return [
        ...options.filter(o => o.isScheduled),
        ...options.filter(o => !o.isScheduled),
      ]
    },
    nominationOptions() {
      return this.nominations.map(n => ({
        text: n.attributes?.name || n.name,
        value: parseInt(n.id, 10),
      }))
    },
    courseOptions() {
      return this.courses.map(c => ({
        text: c.attributes?.name || c.name,
        value: parseInt(c.id, 10),
      }))
    },
    referralOptions() {
      return this.referrals.map(r => ({
        text: r.attributes?.name || r.name,
        value: parseInt(r.id, 10),
      }))
    },
    customerTagOptions() {
      return this.customerTags.map(t => ({
        text: t.attributes?.name || t.name,
        value: parseInt(t.id, 10),
      }))
    },
  },
  watch: {
    value(v) {
      if (v) {
        this.form = this.getInitialForm()
        this.showCancelReason = false
        this.nominationDraft = { userId: null, nominationId: null }
        this.showNominationInput = false
        this.isDetailOpen = this.detailFilledLabels.length > 0
        this.fetchScheduledCasts()
      }
    },
    // 日付が変わったら出勤予定者（キャスト並び順）も追従させる
    'form.reservedDate': function onReservedDateChange() {
      this.fetchScheduledCasts()
    },
  },
  methods: {
    async fetchScheduledCasts() {
      if (!this.form.reservedDate) {
        this.scheduledCastIds = []

        return
      }
      try {
        const res = await WorkScheduleApi.getWorkSchedules({
          status: 'approved',
          requestType: 'work',
          startDate: this.form.reservedDate,
          endDate: this.form.reservedDate,
        })
        this.scheduledCastIds = (res.data?.work_schedules?.data || []).map(d => Number(d.attributes.userId))
      } catch (error) {
        // 並び順の付加情報のため、取得失敗時は通常順のまま
        this.scheduledCastIds = []
      }
    },
    addNomination() {
      const { userId, nominationId } = this.nominationDraft
      if (!userId || !nominationId) return
      const exists = this.form.nominations.find(n => n.userId === userId && n.nominationId === nominationId)
      if (exists) {
        this.$toast.warning('同じ指名が既に追加されています')

        return
      }
      this.form.nominations.push({ userId, nominationId })
      this.nominationDraft = { userId: null, nominationId: null }
      this.showNominationInput = false
    },
    castNameOf(userId) {
      const cast = this.casts.find(c => parseInt(c.id, 10) === Number(userId))

      return cast?.attributes?.name || cast?.name || `ID:${userId}`
    },
    nominationNameOf(nominationId) {
      const nomination = this.nominations.find(n => parseInt(n.id, 10) === Number(nominationId))

      return nomination?.attributes?.name || nomination?.name || `ID:${nominationId}`
    },
    getInitialForm() {
      if (this.reservation) {
        const a = this.reservation.attributes || {}

        return {
          reservedDate: a.reservedDate,
          startTime: this.extractTime(a.startTime),
          endTime: this.extractTime(a.endTime),
          customerCount: a.customerCount || 1,
          hallId: a.hall?.id || null,
          tableNumber: a.tableNumber || null,
          nominations: (a.nominations || []).map(n => ({ userId: n.userId, nominationId: n.nominationId })),
          courseId: a.course?.id || null,
          referralId: a.referral?.id || null,
          customerTagIds: (a.customerTags || []).map(t => t.id),
          customers: (a.customers || []).map(c => ({ ...c })),
          memo: a.memo || '',
          cancelReason: '',
          status: a.status,
        }
      }

      return {
        reservedDate: this.defaultDate || new Date().toISOString().slice(0, 10),
        startTime: '20:00',
        endTime: null,
        customerCount: 1,
        hallId: null,
        tableNumber: null,
        nominations: [],
        courseId: null,
        referralId: null,
        customerTagIds: [],
        customers: [],
        memo: '',
        cancelReason: '',
        status: 'confirmed',
      }
    },
    extractTime(timeStr) {
      if (!timeStr) return null
      const match = timeStr.match(/(\d{2}):(\d{2})/)

      return match ? `${match[1]}:${match[2]}` : null
    },
    addCustomer(customer) {
      this.form.customers.push(customer)
    },
    removeCustomer(id) {
      this.form.customers = this.form.customers.filter(c => c.id !== id)
    },
    buildParams() {
      return buildReservationParams(this.form, {
        isEdit: this.isEdit,
        originalCustomers: this.reservation?.attributes?.customers || [],
        originalTags: this.reservation?.attributes?.customerTags || [],
        // 楽観ロック: 読み込み時点の updatedAt と不一致なら 409 が返る（未取得なら送らず従来挙動）
        expectedUpdatedAt: this.isEdit ? this.reservation?.attributes?.updatedAt : null,
      })
    },
    // 保存時に指名の追加行（ドラフト）を自動確定する（ロジックは reservationPayload.js）
    commitNominationDraft() {
      const result = commitNominationDraft(this.nominationDraft, this.form.nominations)
      if (!result.ok) {
        this.$emit('error', result.error)

        return false
      }
      this.form.nominations = result.nominations
      this.nominationDraft = { userId: null, nominationId: null }

      return true
    },
    // 指名あり×コース未選択の保存・確定・来店処理をブロックする。
    // ドラフトの自動確定（commitNominationDraft）後に呼ぶこと
    validateCourseRequirement() {
      if (this.form.nominations.length > 0 && !this.form.courseId) {
        this.$emit('error', '指名がある予約はコースの選択が必要です（来店処理のセット計上に使われます）')

        return false
      }

      return true
    },
    async save() {
      if (!this.commitNominationDraft()) return
      if (!this.validateCourseRequirement()) return
      this.isSaving = true
      try {
        const params = this.buildParams()
        if (this.form.status) params.status = this.form.status
        await ReservationApi.updateReservation(this.reservation.id, params)
        this.$emit('saved')
        this.close()
      } catch (e) {
        const msg = e.response?.data?.errors?.join(', ') || '保存に失敗しました'
        this.$emit('error', msg)
      } finally {
        this.isSaving = false
      }
    },
    async cancelReservation() {
      if (!this.showCancelReason) {
        this.showCancelReason = true

        return
      }
      if (!this.form.cancelReason) return

      this.isSaving = true
      try {
        await ReservationApi.updateReservation(this.reservation.id, {
          status: 'canceled',
          cancelReason: this.form.cancelReason,
          canceledAt: new Date().toISOString(),
          ...(this.reservation?.attributes?.updatedAt
            ? { expectedUpdatedAt: this.reservation.attributes.updatedAt }
            : {}),
        })
        this.$emit('saved')
        this.close()
      } catch (e) {
        const msg = e.response?.data?.errors?.join(', ') || 'キャンセルに失敗しました'
        this.$emit('error', msg)
      } finally {
        this.isSaving = false
      }
    },
    // 卓作成はウィザード（ホール→卓→確認・修正）に集約するため、親に委譲する。
    // フォームで直したコース等が未保存のままウィザードに渡らないよう、先に保存してから最新行を渡す
    async requestCheckIn() {
      if (!this.commitNominationDraft()) return
      if (!this.validateCourseRequirement()) return
      this.isSaving = true
      try {
        const params = this.buildParams()
        if (this.form.status) params.status = this.form.status
        const { data } = await ReservationApi.updateReservation(this.reservation.id, params)
        const updated = data?.reservation?.data || this.reservation
        this.$emit('check-in-requested', updated)
        this.close()
      } catch (e) {
        const msg = e.response?.data?.errors?.join(', ') || '保存に失敗しました'
        this.$emit('error', msg)
      } finally {
        this.isSaving = false
      }
    },
    async deleteReservation() {
      // eslint-disable-next-line no-alert
      if (!window.confirm('この予約を削除しますか？')) return

      this.isSaving = true
      try {
        await ReservationApi.deleteReservation(this.reservation.id)
        this.$emit('saved')
        this.close()
      } catch (e) {
        const msg = e.response?.data?.errors?.join(', ') || '削除に失敗しました'
        this.$emit('error', msg)
      } finally {
        this.isSaving = false
      }
    },
    close() {
      this.$emit('input', false)
    },
  },
}
</script>

<style scoped>
/* ヘッダーのステータス変更セレクトはタイトル行に収まる幅に抑える */
.status-select {
  max-width: 170px;
  flex: 0 0 auto;
}

/* セクション見出し（右側に区切り線を伸ばす） */
.form-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  margin: 20px 0 14px;
}

.form-section-label::after {
  content: '';
  flex: 1;
  border-top: 1px solid rgba(148, 163, 184, 0.25);
}

.form-section-label.mt-0 {
  margin-top: 0;
}

.nomination-chips {
  gap: 6px;
}

/* 卓は来店時に選ぶ案内 */
.table-later-hint {
  font-size: 11.5px;
  color: #94a3b8;
  margin: -6px 0 8px;
}

/* 指名あり×コース未選択の警告 */
.course-required-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #ef4444;
  background: rgba(239, 68, 68, 0.07);
  border: 1px solid rgba(239, 68, 68, 0.35);
  border-radius: 6px;
  padding: 6px 10px;
}

/* 詳細（任意項目）の開閉トグル */
.detail-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 1px solid rgba(148, 163, 184, 0.25);
  background: rgba(145, 85, 253, 0.06);
  border-radius: 6px;
  padding: 10px 14px;
  font-size: 12.5px;
  font-weight: 600;
  color: inherit;
  cursor: pointer;
  margin: 16px 0;
  text-align: left;
}

.detail-toggle__count {
  font-size: 11.5px;
  font-weight: 400;
  color: #94a3b8;
}

.detail-toggle__caret {
  margin-left: auto !important;
  transition: transform 0.15s;
}

.detail-toggle__caret--open {
  transform: rotate(180deg);
}
</style>
